import { useId, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  DatePicker as MuiDatePicker,
  type DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import Box from '../primitives/Box';
import { fieldLabelSx, outlinedFieldSx } from '../styles/formControlStyles';
import { mergeSx } from '../utils/mergeSx';
import { DATE_PICKER_FORMAT } from './datePickerLocale';

type MuiDatePickerSlotProps = NonNullable<MuiDatePickerProps['slotProps']>;
type TextFieldSlotProps = NonNullable<MuiDatePickerSlotProps['textField']>;

export type DatePickerProps = Omit<MuiDatePickerProps, 'label' | 'slotProps'> & {
  label?: ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  helperText?: ReactNode;
  error?: boolean;
  sx?: SxProps<Theme>;
  slotProps?: Omit<MuiDatePickerSlotProps, 'textField'> & {
    textField?: TextFieldSlotProps;
  };
};

interface FieldLabelProps {
  children: ReactNode;
  required?: boolean;
  htmlFor?: string;
  id?: string;
  sx?: SxProps<Theme>;
  /** MUI InputLabel leftover. External labels sit above the field, so this is ignored. */
  shrink?: boolean;
}

const FieldLabelRoot = styled('label')({});

const FieldLabel = ({ children, required, htmlFor, id, sx }: FieldLabelProps) => (
  <FieldLabelRoot id={id} htmlFor={htmlFor} sx={mergeSx(fieldLabelSx, sx)}>
    {children}
    {required ? ' *' : null}
  </FieldLabelRoot>
);

/**
 * Single-date picker with label above the field (no floating InputLabel).
 * Relies on app-level LocalizationProvider.
 */
const DatePicker = ({
  label,
  required = false,
  fullWidth = true,
  size = 'small',
  helperText,
  error,
  sx,
  slotProps,
  ...props
}: DatePickerProps) => {
  const inputId = useId();
  const { textField: textFieldSlotProps, ...restSlotProps } = slotProps ?? {};
  const textFieldObject =
    textFieldSlotProps && typeof textFieldSlotProps === 'object' ? textFieldSlotProps : undefined;

  const picker = (
    <MuiDatePicker
      format={DATE_PICKER_FORMAT}
      {...props}
      slotProps={{
        ...restSlotProps,
        textField: {
          id: inputId,
          fullWidth,
          size,
          helperText,
          error,
          ...textFieldObject,
          sx: mergeSx(
            outlinedFieldSx,
            textFieldObject?.sx,
            fullWidth ? { width: '100%' } : undefined,
          ),
        },
      }}
    />
  );

  if (label == null) {
    return <Box sx={mergeSx(fullWidth ? { width: '100%' } : undefined, sx)}>{picker}</Box>;
  }

  return (
    <Box
      sx={mergeSx(
        {
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: fullWidth ? '100%' : undefined,
        },
        sx,
      )}
    >
      <FieldLabel htmlFor={inputId} required={required}>
        {label}
      </FieldLabel>
      {picker}
    </Box>
  );
};

export default DatePicker;
